import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Zap, LayoutDashboard, Package, FileText, GitBranch, Tag, ShoppingCart, BarChart3, Settings, LogOut, Sun, Moon, Menu, Sparkles, Percent, Users, Star, Mail, FolderOpen } from 'lucide-react';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/products', icon: Package, label: 'Products' },
  { to: '/admin/categories', icon: FolderOpen, label: 'Categories' },
  { to: '/admin/pages', icon: FileText, label: 'Pages' },
  { to: '/admin/products/generate', icon: Sparkles, label: 'AI Generator', sparky: true },
  { to: '/admin/funnels', icon: GitBranch, label: 'Funnels' },
  { to: '/admin/offers', icon: Tag, label: 'Offers' },
  { to: '/admin/coupons', icon: Percent, label: 'Coupons' },
  { to: '/admin/affiliates', icon: Users, label: 'Affiliates' },
  { to: '/admin/featured', icon: Star, label: 'Featured' },
  { to: '/admin/customers', icon: Mail, label: 'Customers' },
  { to: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

function SidebarContent() {
  const { logoutAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 flex items-center gap-3">
        <img src="/logo.png" alt="Wayfinder" className="w-10 h-10 rounded-xl shrink-0" />
        <div>
          <h1 className="font-bold text-lg tracking-tight font-['Outfit']">Wayfinder</h1>
          <p className="text-xs text-muted-foreground">Business Advisor</p>
        </div>
      </div>
      <Separator />
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group
                ${isActive ? 'bg-[#1A9DA5]/10 text-[#1A9DA5]' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}
                ${item.sparky ? 'border border-dashed border-[#1A9DA5]/30' : ''}`
              }
            >
              <item.icon className={`w-4 h-4 ${item.sparky ? 'text-[#1A9DA5]' : ''}`} />
              {item.label}
              {item.sparky && <Zap className="w-3 h-3 text-[#1A9DA5] ml-auto" />}
            </NavLink>
          ))}
        </nav>
      </ScrollArea>
      <Separator />
      <div className="p-3 space-y-1">
        <Button data-testid="theme-toggle" variant="ghost" size="sm" className="w-full justify-start gap-3" onClick={toggleTheme}>
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </Button>
        <Button variant="ghost" size="sm" className="w-full justify-start gap-3 text-muted-foreground" onClick={() => window.open('/', '_blank')}>
          <Package className="w-4 h-4" />
          View Storefront
        </Button>
        <Button data-testid="logout-btn" variant="ghost" size="sm" className="w-full justify-start gap-3 text-destructive" onClick={() => { logoutAdmin(); navigate('/admin/login'); }}>
          <LogOut className="w-4 h-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden" data-testid="admin-layout">
      <aside className="hidden lg:flex w-64 border-r bg-card flex-col shrink-0">
        <SidebarContent />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b flex items-center px-4 gap-3">
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon"><Menu className="w-5 h-5" /></Button>
          </SheetTrigger>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Wayfinder" className="w-6 h-6 rounded" />
            <span className="font-bold font-['Outfit']">Wayfinder</span>
          </div>
        </div>
        <SheetContent side="left" className="w-64 p-0">
          <SidebarContent />
        </SheetContent>
      </Sheet>
      <main className="flex-1 overflow-auto lg:pt-0 pt-14">
        <div className="p-6 md:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
